import { useAuthStore } from '@/stores/authStore';
import { Route, Routes, Navigate } from 'react-router-dom';
import { RouteMap } from './Routes';
import Login from '@/pages/Login/Login';
import PlanAndPayments from '@/pages/PlanAndPayments/PlanAndPayments';
import PrivateLayout from '@/components/Layout/PrivateLayout';
import MyNotes from '@/pages/MyNotes/MyNotes';
import SharedWithMe from '@/pages/SharedWithMe/SharedWithMe';
import Shared from '@/pages/Shared/Shared';
import PublicNote from '@/pages/PublicNote/PublicNote';
import AccountSettings from '@/pages/Settings/AccountSettings';
import ManageTagsPage from '@/pages/Settings/ManageTagsPage';
import DesktopAppPage from '@/pages/Settings/DesktopAppPage';
import Support from '@/pages/Support/Support';
import Home from '@/pages/Home/Home';
import Note from '@/pages/Note/Note';
import Error from '@/pages/Error/Error';
import ProtectedRoute from './ProtectedRoutes';
import { AccountStatusTypes } from '@/helpers/accountStatus';
import CheckoutForm from '@/pages/CheckoutForm/CheckoutForm';
import CheckoutSuccess from '@/pages/CheckoutSuccess/CheckoutSuccess';
import EmailAction from '@/pages/EmailAction/EmailAction';
import Beta from '@/pages/Beta/Beta';
import VideoUploadPage from '@/pages/Beta/VideoUploadPage';
import VideoRecordPage from '@/pages/Record/VideoRecordPage';
import AboutMePage from '@/pages/Beta/AboutMe';
import ScreenRecordingPage from '@/pages/Beta/ScreenRecordingPage';
import SecondBrainPage from '@/pages/Beta/SecondBrainPage';
import Loading from '@/components/Loading';
import { Stack } from '@mui/material';
import TrialWelcome from '@/components/TrialWelcome/TrialWelcome';
import { BETA_FOLDERS_FEATURE_ENABLED } from '@/features/betaProjects/config';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '@/features/betaOrganizations/config';
import { SECOND_BRAIN_FEATURE_ENABLED } from '@/features/secondBrain/config';
import { VIBE_CODE_FEATURE_ENABLED } from '@/features/vibeCode/config';
import { ZAPIER_FEATURE_ENABLED } from '@/features/zapier/config';
import { SMART_TAGS_FEATURE_ENABLED } from '@/features/betaSmartTags/config';
import ZapierSettingsPage from '@/features/zapier/pages/ZapierSettingsPage';
import SmartTagsPage from '@/features/betaSmartTags/SmartTagsPage';
import ProjectsPage from '@/features/betaProjects/pages/ProjectsPage';
import ProjectNotesPage from '@/features/betaProjects/pages/ProjectNotesPage';
import OrganizationsPage from '@/features/betaOrganizations/pages/OrganizationsPage';
import OrganizationNotesPage from '@/features/betaOrganizations/pages/OrganizationNotesPage';
import VibeCodePage from '@/pages/Beta/VibeCodePage';
import AudioRecordingPage from '@/features/betaAudioRecording/AudioRecordingPage';
import OnboardingPage from '@/features/onboarding/OnboardingPage';
import PluginRedirect from '@/pages/PluginRedirect';
import Playground from '@/pages/Playground';
import DesktopAuth from '@/pages/DesktopAuth/DesktopAuth';
import SavedNotes from '@/pages/SavedNotes/SavedNotes';
import FirstVideoPage from '@/pages/FirstVideo';
import AcceptInvite from '@/pages/AcceptInvite/AcceptInvite';
import WhatsNewFeed from '@/pages/WhatsNew/WhatsNewFeed';
import WhatsNewDetail from '@/pages/WhatsNew/WhatsNewDetail';

const AppRoutes = () => {
  const { accountStatus, loading, userData } = useAuthStore();

  return (
    <Routes>
      <Route path={RouteMap.LOGIN} element={<Login />} />
      <Route path={RouteMap.DESKTOP_AUTH} element={<DesktopAuth />} />
      <Route path={RouteMap.EMAIL_ACTION} element={<EmailAction />} />
      {/* Public org-invite landing page — handles auth bounce internally */}
      <Route path={RouteMap.ACCEPT_INVITE} element={<AcceptInvite />} />
      <Route element={<ProtectedRoute />}>
        {/* First Video — standalone (no navbar/drawer) so the extension can pop cleanly */}
        <Route path={RouteMap.FIRST_VIDEO} element={<FirstVideoPage />} />
        <Route path={RouteMap.ROOT} element={<PrivateLayout />}>
          <Route index element={<Navigate to={RouteMap.HOME} replace />} />
          <Route path={RouteMap.ONBOARDING} element={<OnboardingPage />} />

          {/* Show loading state while checking user data */}

          {loading || (!userData && accountStatus === null) ? (
            <Route
              path="*"
              element={
                <Stack
                  style={{
                    height: '90vh',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Loading />
                </Stack>
              }
            />
          ) : (
            <>
              {/* Account management remains available when a plan expires, including
                  desktop handoffs for email, avatar, password, and deletion. */}
              <Route path={RouteMap.SETTINGS} element={<AccountSettings />} />
              {accountStatus !== AccountStatusTypes.TRIAL && accountStatus !== AccountStatusTypes.ACTIVE && (
                <>
                  <Route path={RouteMap.HOME} element={<PlanAndPayments />} />
                  {/* The Drawer's Desktop App link is gated on the plan feature, not on
                      account status, so a lapsed-but-entitled user can still click it —
                      without this it falls through to the top-level Error route, which
                      renders outside PrivateLayout and strips their navigation. */}
                  <Route path={RouteMap.SETTINGS_DESKTOP_APP} element={<PlanAndPayments />} />
                  <Route path={RouteMap.SUPPORT} element={<Support />} />
                  <Route path={RouteMap.PLAN} element={<PlanAndPayments />} />
                  <Route path={RouteMap.MY_NOTES} element={<PlanAndPayments />} />
                  <Route path={RouteMap.SHARED_WITH_ME} element={<PlanAndPayments />} />
                  <Route path={RouteMap.SAVED_NOTES} element={<PlanAndPayments />} />
                  <Route path={RouteMap.FAVORITES} element={<PlanAndPayments />} />
                  <Route path={`${RouteMap.CHECKOUT}/:customerSecret`} element={<CheckoutForm />} />
                  <Route path={RouteMap.CHECKOUT_SUCCESS} element={<CheckoutSuccess />} />
                  <Route path={`${RouteMap.NOTE}/:noteId`} element={<PlanAndPayments />} />
                </>
              )}
              {(accountStatus === AccountStatusTypes.TRIAL || accountStatus === AccountStatusTypes.ACTIVE) && (
                <>
                  <Route path={RouteMap.HOME} element={<Home />} />
                  <Route path={RouteMap.SETTINGS_TAGS} element={<ManageTagsPage />} />
                  <Route path={RouteMap.SETTINGS_DESKTOP_APP} element={<DesktopAppPage />} />
                  <Route path={RouteMap.SUPPORT} element={<Support />} />
                  <Route path={RouteMap.PLAN} element={<PlanAndPayments />} />
                  <Route path={`${RouteMap.CHECKOUT}/:customerSecret`} element={<CheckoutForm />} />
                  <Route path={RouteMap.CHECKOUT_SUCCESS} element={<CheckoutSuccess />} />
                  <Route path={RouteMap.MY_NOTES} element={<MyNotes />} />
                  <Route path={RouteMap.BETA} element={<Beta />} />
                  <Route path={RouteMap.RECORD} element={<VideoRecordPage />} />
                  <Route path={RouteMap.AUDIO_RECORDING} element={<AudioRecordingPage />} />
                  <Route path={RouteMap.BETA_VIDEO_UPLOAD} element={<VideoUploadPage />} />
                  <Route path={RouteMap.BETA_SCREEN_RECORDING} element={<ScreenRecordingPage />} />
                  <Route path={RouteMap.BETA_MODEL_GUIDANCE} element={<AboutMePage />} />
                  {SECOND_BRAIN_FEATURE_ENABLED && (
                    <Route path={RouteMap.BETA_SECOND_BRAIN} element={<SecondBrainPage />} />
                  )}
                  {VIBE_CODE_FEATURE_ENABLED && <Route path={RouteMap.BETA_VIBE_CODE} element={<VibeCodePage />} />}
                  {ZAPIER_FEATURE_ENABLED && <Route path={RouteMap.BETA_ZAPIER} element={<ZapierSettingsPage />} />}
                  {SMART_TAGS_FEATURE_ENABLED && <Route path={RouteMap.BETA_SMART_TAGS} element={<SmartTagsPage />} />}
                  {/* Redirect old beta audio route to new location */}
                  <Route
                    path={RouteMap.BETA_AUDIO_RECORDING}
                    element={<Navigate to={RouteMap.AUDIO_RECORDING} replace />}
                  />

                  {BETA_FOLDERS_FEATURE_ENABLED && (
                    <>
                      <Route path={RouteMap.FOLDERS} element={<ProjectsPage />} />
                      <Route path={RouteMap.FOLDER_DETAIL} element={<ProjectNotesPage />} />
                    </>
                  )}
                  {BETA_ORGANIZATIONS_FEATURE_ENABLED && (
                    <>
                      <Route path={RouteMap.ORGANIZATIONS} element={<OrganizationsPage />} />
                      <Route path={RouteMap.ORGANIZATION_DETAIL} element={<OrganizationNotesPage />} />
                    </>
                  )}
                  <Route path={RouteMap.TRIAL_WELCOME} element={<TrialWelcome />} />

                  <Route path={RouteMap.SHARED_WITH_ME} element={<SharedWithMe />} />
                  <Route path={RouteMap.SAVED_NOTES} element={<SavedNotes />} />
                  <Route path={RouteMap.FAVORITES} element={<MyNotes />} />
                  <Route path={`${RouteMap.NOTE}/:noteId`} element={<Note />} />
                </>
              )}
            </>
          )}
        </Route>
      </Route>

      {/* Public/shared note route uses PrivateLayout with drawer hidden */}
      <Route element={<PrivateLayout />}>
        <Route path="/shared/:token" element={<Shared />} />
      </Route>

      {/* Public note route - standalone layout without sidebar or auth */}
      <Route path={RouteMap.PUBLIC_NOTE} element={<PublicNote />} />

      {/* Plugin redirect handler - public route for extension redirects */}
      <Route path={RouteMap.PLUGIN_REDIRECT} element={<PluginRedirect />} />

      {/* Playground - landing page for extension users on protected pages */}
      <Route path={RouteMap.PLAYGROUND} element={<Playground />} />

      {/* What's New blog — public, no auth required; uses PrivateLayout so it keeps the app shell */}
      <Route element={<PrivateLayout />}>
        <Route path={RouteMap.WHATS_NEW} element={<WhatsNewFeed />} />
        <Route path={RouteMap.WHATS_NEW_DETAIL} element={<WhatsNewDetail />} />
      </Route>

      <Route path="*" element={<Error />} />
    </Routes>
  );
};

export default AppRoutes;
